<template>
  <!-- eslint-disable vue/no-v-html -->
  <div v-if="tag === 'div'"
       class="markdown"
       v-bind="$attrs"
       v-html="html"
  ></div>
  <span v-else-if="tag === 'span'"
        class="markdown"
        v-bind="$attrs"
        v-html="html"
  ></span>
  <p v-else
     v-bind="$attrs"
     class="markdown"
     v-html="html"
  ></p>
  <!--eslint-enable-->
</template>

<script lang="ts" setup>
import { marked } from 'marked';
import { computed } from 'vue';
import * as xss from 'xss';

const allowedTags = [
    'address',
    'article',
    'aside',
    'footer',
    'header',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'hgroup',
    'main',
    'nav',
    'section',
    'blockquote',
    'dd',
    'div',
    'dl',
    'dt',
    'figcaption',
    'figure',
    'hr',
    'li',
    'main',
    'ol',
    'p',
    'pre',
    'ul',
    'a',
    'abbr',
    'b',
    'bdi',
    'bdo',
    'br',
    'cite',
    'code',
    'data',
    'dfn',
    'em',
    'i',
    'kbd',
    'mark',
    'q',
    'rb',
    'rp',
    'rt',
    'rtc',
    'ruby',
    's',
    'samp',
    'small',
    'span',
    'strong',
    'sub',
    'sup',
    'time',
    'u',
    'var',
    'wbr',
    'caption',
    'col',
    'colgroup',
    'table',
    'tbody',
    'td',
    'tfoot',
    'th',
    'thead',
    'tr'
];

const voidTags = [
    'img',
    'br',
    'hr',
    'area',
    'base',
    'basefont',
    'input',
    'link',
    'meta'
];

const allowedProtocols = [
    'http:',
    'https:',
    'mailto:',
    'tel:'
];

function checkUrl(value: string) {
    try {
        const url = new URL(
            value,
            location.toString()
        );
        return allowedProtocols.includes(url.protocol);
    }
    catch (e) {
        return false;
    }
}

function sanitize(html: string) {
    const whiteList: xss.IWhiteList = {
        ...Object.fromEntries(allowedTags.map(tag => [
            tag,
            []
        ]))
    };
    const stack: string[] = [];
    html = xss.filterXSS(
        html,
        {
            whiteList,
            stripIgnoreTag: true,
            onTag(tag, raw, options) {
                let html: string | undefined;
                if (tag === 'a' && !options.isClosing) {
                    /* eslint-disable */
                const attrs: any = {};
                xss.parseAttr(raw.slice(3), (name, value) => {
                    if (name === 'href') {
                        attrs[name] = checkUrl(value) ? value : '#';
                    } else if (name === 'title') {
                        attrs[name] = xss.escapeAttrValue(value);
                    }
                    return '';
                });
                attrs.rel = 'noopener noreferrer';
                attrs.target = '_blank';
                html = `<a ${Object.entries(attrs).map(([name, value]) => `${name}="${value}"`).join(' ')}>`;
            }
            if (raw.endsWith('/>') || voidTags.includes(tag)) return;
            if (!options.isClosing) {
                stack.push(tag);
                return html;
            }
            let result = '';
            while (stack.length) {
                const last = stack.pop();
                if (last === tag) {
                    return result + raw;
                }
                result += `</${last}>`;
            }
            return raw.replace(/</g, '&lt;').replace(/>/g, '&gt;');
        }
    });
    while (stack.length) {
        const last = stack.pop();
        html += `</${last}>`;
    }
    return html;
}

const props = defineProps<{
    source: string,
    inline: boolean,
    tag: string,
    unsafe: boolean
}>();

const html = computed(() => {
    let rawHtml = props.inline
        ? marked.parseInline(props.source || '') as string
        : marked.parse(props.source || '') as string;
    if (!props.unsafe) rawHtml = sanitize(rawHtml);
    return rawHtml;
});

const tag = computed(() => props.tag || (props.inline ? 'span' : 'div'));
</script>
